import React, { useState } from "react";
import FilterSection from "./FilterSection";
import RangeSlider from "./RangeSlider";

const FiltersSidebar = ({
  filterOptions,
  expandedFilters,
  selectedBrand,
  selectedYear,
  selectedFuel,
  selectedTransmission,
  minPrice,
  maxPrice,
  minKm,
  maxKm,
  onToggleFilter,
  onCloseFilter,
  onBrandChange,
  onYearChange,
  onFuelChange,
  onTransmissionChange,
  onMinPriceChange,
  onMaxPriceChange,
  onMinKmChange,
  onMaxKmChange,
  onClearFilters,
  getFilterCount
}) => {
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const toggleMobileFilters = () => {
    setIsMobileFiltersOpen(!isMobileFiltersOpen);
  };

  const handleBrandToggle = (brand) => {
    onBrandChange(brand);
  };

  const handleClearFilters = () => {
    onClearFilters();
    setIsMobileFiltersOpen(false); // Close mobile filters after clearing
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedBrand !== 'all') count++;
    if (selectedYear !== 'all') count++;
    if (selectedFuel !== 'all') count++;
    if (selectedTransmission !== 'all') count++;
    if (minPrice || maxPrice) count++;
    if (minKm || maxKm) count++;
    return count;
  };

  return (
    <div className="lg:w-1/4 flex-shrink-0">
      {/* Mobile Toggle Button */}
      <div className="lg:hidden mb-4">
        <button
          onClick={toggleMobileFilters}
          className="w-full bg-sky-50 rounded-xl shadow-md border border-sky-200 p-4 flex justify-between items-center hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className="font-medium text-sky-700">Filtros</span>
            {getActiveFilterCount() > 0 && (
              <span className="bg-sky-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                {getActiveFilterCount()}
              </span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-sky-600 transition-transform duration-200 ${isMobileFiltersOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Filters Content */}
      {isMobileFiltersOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/30 backdrop-blur-sm"
          onClick={toggleMobileFilters}
        >
          <div
            className="absolute top-0 left-0 right-0 bg-white rounded-b-2xl shadow-2xl transform transition-transform duration-300 ease-out flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{
              transform: isMobileFiltersOpen ? 'translateY(0)' : 'translateY(-100%)'
            }}
          >
            <div className="bg-white rounded-t-2xl shadow-lg border border-slate-200 p-4 flex-1 max-h-[70vh] overflow-y-auto">
              {/* Header */}
              <div className="flex justify-between items-center mb-4 pb-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-800">Filtros</h2>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={toggleMobileFilters}
                    className="px-3 py-1 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors duration-200 min-w-[60px]"
                  >
                    Cerrar
                  </button>
                  <button
                    onClick={handleClearFilters}
                    className="px-3 py-1 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors duration-200 min-w-[60px]"
                  >
                    Limpiar
                  </button>
                </div>
              </div>

              {/* Brand Filter */}
              <FilterSection
                title="Marca"
                expanded={expandedFilters.brand}
                onToggle={() => onToggleFilter("brand")}
                onClose={() => onCloseFilter("brand")}
              >
                <div className="space-y-1">
                  {filterOptions.brands
                    .filter((brand) => brand !== "all")
                    .map((brand) => {
                      const count = getFilterCount("brand", brand);
                      return (
                        <label
                          key={brand}
                          className="flex items-center justify-between cursor-pointer p-1 rounded-lg hover:bg-white transition-colors duration-200"
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedBrand === brand}
                              onChange={() => handleBrandToggle(brand)}
                              className="w-4 h-4 text-sky-600 border-slate-300 rounded focus:ring-2 focus:ring-sky-500 focus:ring-offset-0"
                            />
                            <span className="text-sm text-slate-700 font-medium">
                              {brand}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded-full border border-slate-200">
                            {count}
                          </span>
                        </label>
                      );
                    })}
                </div>
              </FilterSection>

              {/* Year Filter */}
              <FilterSection
                title="Año"
                expanded={expandedFilters.year}
                onToggle={() => onToggleFilter("year")}
                onClose={() => onCloseFilter("year")}
              >
                <div className="space-y-1">
                  {filterOptions.years
                    .filter((year) => year !== "all")
                    .map((year) => {
                      const count = getFilterCount("year", year);
                      return (
                        <label
                          key={year}
                          className="flex items-center justify-between cursor-pointer p-1 rounded-lg hover:bg-white transition-colors duration-200"
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedYear === year}
                            onChange={() =>
                              onYearChange(selectedYear === year ? "all" : year)
                            }
                              className="w-4 h-4 text-sky-600 border-slate-300 rounded focus:ring-2 focus:ring-sky-500 focus:ring-offset-0"
                            />
                            <span className="text-sm text-slate-700 font-medium">
                              {year}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded-full border border-slate-200">
                            {count}
                          </span>
                        </label>
                      );
                    })}
                </div>
              </FilterSection>

              {/* Fuel Filter */}
              <FilterSection
                title="Combustible"
                expanded={expandedFilters.fuel}
                onToggle={() => onToggleFilter("fuel")}
                onClose={() => onCloseFilter("fuel")}
              >
                <div className="space-y-1">
                  {filterOptions.fuels
                    .filter((fuel) => fuel !== "all")
                    .map((fuel) => {
                      const count = getFilterCount("fuel", fuel);
                      return (
                        <label
                          key={fuel}
                          className="flex items-center justify-between cursor-pointer p-1 rounded-lg hover:bg-white transition-colors duration-200"
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedFuel === fuel}
                              onChange={() =>
                                onFuelChange(selectedFuel === fuel ? "all" : fuel)
                              }
                              className="w-4 h-4 text-sky-600 border-slate-300 rounded focus:ring-2 focus:ring-sky-500 focus:ring-offset-0"
                            />
                            <span className="text-sm text-slate-700 font-medium">
                              {fuel}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded-full border border-slate-200">
                            {count}
                          </span>
                        </label>
                      );
                    })}
                </div>
              </FilterSection>

              {/* Transmission Filter */}
              <FilterSection
                title="Transmisión"
                expanded={expandedFilters.transmission}
                onToggle={() => onToggleFilter("transmission")}
                onClose={() => onCloseFilter("transmission")}
              >
                <div className="space-y-1">
                  {filterOptions.transmissions
                    .filter((transmission) => transmission !== "all")
                    .map((transmission) => {
                      const count = getFilterCount(
                        "transmission",
                        transmission,
                      );
                      return (
                        <label
                          key={transmission}
                          className="flex items-center justify-between cursor-pointer p-1 rounded-lg hover:bg-white transition-colors duration-200"
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={selectedTransmission === transmission}
                              onChange={() =>
                                onTransmissionChange(
                                  selectedTransmission === transmission
                                    ? "all"
                                    : transmission,
                                )
                              }
                              className="w-4 h-4 text-sky-600 border-slate-300 rounded focus:ring-2 focus:ring-sky-500 focus:ring-offset-0"
                            />
                            <span className="text-sm text-slate-700 font-medium">
                              {transmission}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded-full border border-slate-200">
                            {count}
                          </span>
                        </label>
                      );
                    })}
                </div>
              </FilterSection>

              {/* Kilometers Range */}
              <FilterSection
                title="Kilómetros"
                expanded={expandedFilters.kilometers}
                onToggle={() => onToggleFilter("kilometers")}
                onClose={() => onCloseFilter("kilometers")}
              >
                <RangeSlider
                  min={0}
                  max={500000}
                  valueMin={minKm ? parseInt(minKm) : null}
                  valueMax={maxKm ? parseInt(maxKm) : null}
                  onChangeMin={onMinKmChange}
                  onChangeMax={onMaxKmChange}
                  step={5000}
                  unit=" km"
                />
              </FilterSection>

              {/* Price Range */}
              <FilterSection
                title="Precio (ARS)"
                expanded={expandedFilters.price}
                onToggle={() => onToggleFilter("price")}
                onClose={() => onCloseFilter("price")}
              >
                <RangeSlider
                  min={0}
                  max={50000000}
                  valueMin={minPrice ? parseInt(minPrice) : null}
                  valueMax={maxPrice ? parseInt(maxPrice) : null}
                  onChangeMin={onMinPriceChange}
                  onChangeMax={onMaxPriceChange}
                  step={1000}
                  unit=" ARS"
                />
              </FilterSection>
            </div>
            
            {/* Sticky Apply Button */}
            <div className="bg-white border-t border-slate-200 p-4 rounded-b-2xl shadow-lg">
              <button
                onClick={toggleMobileFilters}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-4 rounded-xl transition-colors duration-200 shadow-lg"
              >
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Filters */}
      <div
        className="hidden lg:block bg-white rounded-2xl shadow-lg border border-slate-200 p-4 
    w-[280px] flex-shrink-0 
      lg:sticky lg:top-8 "
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-slate-800">Filtros</h2>
          <button
            onClick={onClearFilters}
            className="px-3 py-1 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 transition-colors duration-200"
          >
            Limpiar todo
          </button>
        </div>

        {/* Brand Filter */}
        <FilterSection
          title="Marca"
          expanded={expandedFilters.brand}
          onToggle={() => onToggleFilter("brand")}
          onClose={() => onCloseFilter("brand")}
        >
          <div className="space-y-1">
            {filterOptions.brands
              .filter((brand) => brand !== "all")
              .map((brand) => {
                const count = getFilterCount("brand", brand);
                return (
                  <label
                    key={brand}
                    className="flex items-center justify-between cursor-pointer p-1 rounded-lg hover:bg-white transition-colors duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedBrand === brand}
                        onChange={() => handleBrandToggle(brand)}
                        className="w-4 h-4 text-sky-600 border-slate-300 rounded focus:ring-2 focus:ring-sky-500 focus:ring-offset-0"
                      />
                      <span className="text-sm text-slate-700 font-medium">
                        {brand}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded-full border border-slate-200">
                      {count}
                    </span>
                  </label>
                );
              })}
          </div>
        </FilterSection>

        {/* Year Filter */}
        <FilterSection
          title="Año"
          expanded={expandedFilters.year}
          onToggle={() => onToggleFilter("year")}
          onClose={() => onCloseFilter("year")}
        >
          <div className="space-y-1">
            {filterOptions.years
              .filter((year) => year !== "all")
              .map((year) => {
                const count = getFilterCount("year", year);
                return (
                  <label
                    key={year}
                    className="flex items-center justify-between cursor-pointer p-1 rounded-lg hover:bg-white transition-colors duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedYear === year}
                        onChange={() =>
                          onYearChange(selectedYear === year ? "all" : year)
                        }
                        className="w-4 h-4 text-sky-600 border-slate-300 rounded focus:ring-2 focus:ring-sky-500 focus:ring-offset-0"
                      />
                      <span className="text-sm text-slate-700 font-medium">
                        {year}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded-full border border-slate-200">
                      {count}
                    </span>
                  </label>
                );
              })}
          </div>
        </FilterSection>

        {/* Fuel Filter */}
        <FilterSection
          title="Combustible"
          expanded={expandedFilters.fuel}
          onToggle={() => onToggleFilter("fuel")}
          onClose={() => onCloseFilter("fuel")}
        >
          <div className="space-y-1">
            {filterOptions.fuels
              .filter((fuel) => fuel !== "all")
              .map((fuel) => {
                const count = getFilterCount("fuel", fuel);
                return (
                  <label
                    key={fuel}
                    className="flex items-center justify-between cursor-pointer p-1 rounded-lg hover:bg-white transition-colors duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedFuel === fuel}
                        onChange={() =>
                          onFuelChange(selectedFuel === fuel ? "all" : fuel)
                        }
                        className="w-4 h-4 text-sky-600 border-slate-300 rounded focus:ring-2 focus:ring-sky-500 focus:ring-offset-0"
                      />
                      <span className="text-sm text-slate-700 font-medium">
                        {fuel}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded-full border border-slate-200">
                      {count}
                    </span>
                  </label>
                );
              })}
          </div>
        </FilterSection>

        {/* Transmission Filter */}
        <FilterSection
          title="Transmisión"
          expanded={expandedFilters.transmission}
          onToggle={() => onToggleFilter("transmission")}
          onClose={() => onCloseFilter("transmission")}
        >
          <div className="space-y-1">
            {filterOptions.transmissions
              .filter((transmission) => transmission !== "all")
              .map((transmission) => {
                const count = getFilterCount("transmission", transmission);
                return (
                  <label
                    key={transmission}
                    className="flex items-center justify-between cursor-pointer p-1 rounded-lg hover:bg-white transition-colors duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedTransmission === transmission}
                        onChange={() =>
                          onTransmissionChange(
                            selectedTransmission === transmission
                              ? "all"
                              : transmission,
                          )
                        }
                        className="w-4 h-4 text-sky-600 border-slate-300 rounded focus:ring-2 focus:ring-sky-500 focus:ring-offset-0"
                      />
                      <span className="text-sm text-slate-700 font-medium">
                        {transmission}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded-full border border-slate-200">
                      {count}
                    </span>
                  </label>
                );
              })}
          </div>
        </FilterSection>

        {/* Kilometers Range */}
        <FilterSection
          title="Kilómetros"
          expanded={expandedFilters.kilometers}
          onToggle={() => onToggleFilter("kilometers")}
          onClose={() => onCloseFilter("kilometers")}
        >
          <RangeSlider
            min={0}
            max={200000}
            valueMin={minKm ? parseInt(minKm) : null}
            valueMax={maxKm ? parseInt(maxKm) : null}
            onChangeMin={onMinKmChange}
            onChangeMax={onMaxKmChange}
            step={5000}
            unit=" km"
          />
        </FilterSection>

        {/* Price Range */}
        <FilterSection
          title="Precio (ARS)"
          expanded={expandedFilters.price}
          onToggle={() => onToggleFilter("price")}
          onClose={() => onCloseFilter("price")}
        >
          <RangeSlider
            min={0}
            max={50000000}
            valueMin={minPrice ? parseInt(minPrice) : null}
            valueMax={maxPrice ? parseInt(maxPrice) : null}
            onChangeMin={onMinPriceChange}
            onChangeMax={onMaxPriceChange}
            step={1000}
            unit=" ARS"
          />
        </FilterSection>
      </div>
    </div>
  );
};

export default FiltersSidebar;
